import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import type { Kpi } from '../dashboards.api';
import { formatValue, toneOf } from '../dashboards.utils';
import { useCanOpen } from './MaybeLink';

/**
 * One headline number: label, value with its unit, an optional hint, and the tone as a coloured
 * edge plus an icon (never colour alone). Opens its link when the user can open it.
 */
export default function KpiTile({ kpi, dense = false }: { kpi: Kpi; dense?: boolean }) {
  const navigate = useNavigate();
  const canOpen = useCanOpen();
  const tone = toneOf(kpi.tone);
  const linked = canOpen(kpi.link);
  const value = formatValue(kpi.value, kpi.unit);

  return (
    <Box
      role={linked ? 'link' : undefined}
      tabIndex={linked ? 0 : undefined}
      onClick={linked ? () => navigate(kpi.link!) : undefined}
      onKeyDown={
        linked
          ? (e) => {
              if (e.key === 'Enter') navigate(kpi.link!);
            }
          : undefined
      }
      title={linked ? `Open ${kpi.label}` : undefined}
      sx={{
        position: 'relative',
        minWidth: 0,
        height: '100%',
        px: dense ? 1.5 : 2,
        py: dense ? 1 : 1.75,
        pl: dense ? 1.75 : 2.25,
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        cursor: linked ? 'pointer' : 'default',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: kpi.tone && kpi.tone !== 'NEUTRAL' ? tone.color : 'transparent',
        },
        ...(linked && {
          '&:hover, &:focus-visible': { borderColor: 'primary.light', boxShadow: 1, outline: 'none' },
        }),
      }}
    >
      <Stack gap={0.25}>
        <Stack direction='row' alignItems='center' gap={0.75} sx={{ minWidth: 0 }}>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={kpi.label}
          >
            {kpi.label}
          </Typography>
          {tone.icon && (
            <Box component='span' sx={{ color: tone.color, display: 'inline-flex', flexShrink: 0 }} aria-label={tone.label} role='img'>
              <Icon icon={tone.icon} width={16} />
            </Box>
          )}
          {linked && (
            <Box component='span' sx={{ color: 'text.secondary', display: 'inline-flex', flexShrink: 0 }}>
              <Icon icon='mdi:arrow-top-right' width={14} />
            </Box>
          )}
        </Stack>
        <Typography
          sx={{ fontSize: dense ? '1.25rem' : '1.625rem', fontWeight: 600, lineHeight: 1.25, color: 'text.primary', whiteSpace: 'nowrap' }}
        >
          {value}
        </Typography>
        {kpi.hint && !dense && (
          <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.35 }}>
            {kpi.hint}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export function KpiTileSkeleton({ dense = false }: { dense?: boolean }) {
  return (
    <Box sx={{ px: 2, py: dense ? 1 : 1.75, border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
      <Skeleton variant='text' width='60%' />
      <Skeleton variant='text' width='40%' height={dense ? 30 : 40} />
      {!dense && <Skeleton variant='text' width='80%' />}
    </Box>
  );
}
